/**
 * The card that says what the scan is looking at.
 *
 * The tour flew to each incident and told you nothing about it. From across the
 * room — which is what a board like this is for — you saw the camera move and
 * never learned why it had moved there. Everything needed was already on the
 * layer's own describe(), the same text the hover tooltip shows; it just was
 * never asked for.
 *
 * Every line types itself in, in order: the counter, the title, then each label
 * and value down the card. That is slower than showing the text at once, and
 * the slowness is the feature — the eye follows a line being written, which is
 * what makes a new stop read as a new stop rather than as text quietly changing
 * in place. The bar along the bottom runs for the dwell, so the wait is finite.
 */

const el = (id) => document.getElementById(id);

/**
 * Milliseconds per character.
 *
 * The budget is the 15s dwell: this card is about 135 characters, so 34ms a
 * character plus the line gaps lands near six seconds and leaves nine to read
 * it. Faster than this and the typing stops reading as writing and starts
 * reading as a flicker.
 */
const CHAR_MS = 34;

/** Pause between one line finishing and the next starting. */
const LINE_GAP_MS = 150;

export function mountScanCard({ dwellMs = 15000 } = {}) {
  const card = el("scan-card");
  const indexEl = el("scan-card-index");
  const titleEl = el("scan-card-title");
  const rowsEl = el("scan-card-rows");
  const progress = el("scan-card-progress");
  if (!card) return { show() {}, hide() {} };

  /**
   * Types a list of [node, text] pairs one after another.
   *
   * A single timer walking a queue, rather than one timer per line: lines have
   * to arrive in order, and N independent timers racing each other is how you
   * get the value of row three appearing before its label. Cancelling is also
   * one operation, which matters because the next stop can arrive at any point
   * mid-sentence.
   */
  let timer = null;

  function typeSequence(lines) {
    if (timer) clearInterval(timer);

    for (const [node] of lines) {
      node.textContent = "";
      node.classList.remove("typing");
    }

    let line = 0;
    let char = 0;
    let gap = 0;

    timer = setInterval(() => {
      if (line >= lines.length) {
        clearInterval(timer);
        timer = null;
        return;
      }
      if (gap > 0) { gap -= 1; return; }

      const [node, text] = lines[line];
      const chars = [...text];

      // The caret marks the line being written, so exactly one line carries it.
      if (char === 0) node.classList.add("typing");

      char += 1;
      node.textContent = chars.slice(0, char).join("");

      if (char >= chars.length) {
        node.classList.remove("typing");
        line += 1;
        char = 0;
        gap = Math.round(LINE_GAP_MS / CHAR_MS);
      }
    }, CHAR_MS);
  }

  /**
   * Restart a CSS animation that is already on the element.
   *
   * Re-adding a class the element still has plays nothing at all: the class
   * never changed, so no animation begins. Removing it and reading a layout
   * property forces the style to settle in between, which is what makes the
   * second stop animate like the first.
   */
  function replay(node, className) {
    node.classList.remove(className);
    void node.offsetWidth;
    node.classList.add(className);
  }

  return {
    /**
     * @param {{title: string, rows: Array<[string, string]>}} detail
     * @param {{index: number, total: number}} position
     */
    show(detail, position) {
      if (!detail) return this.hide();

      const lines = [];
      lines.push([indexEl, position
        ? `SCANNING ${position.index} / ${position.total}`
        : "SCANNING"]);
      lines.push([titleEl, detail.title ?? ""]);

      rowsEl.replaceChildren();
      for (const [label, value] of detail.rows ?? []) {
        if (value == null || value === "") continue;
        const dt = document.createElement("dt");
        const dd = document.createElement("dd");
        rowsEl.append(dt, dd);
        lines.push([dt, String(label)], [dd, String(value)]);
      }

      card.hidden = false;
      // The dwell drives the bar, so the two can never disagree about how long
      // a stop lasts.
      progress.style.animationDuration = `${dwellMs}ms`;
      replay(progress, "running");
      replay(card, "arriving");
      typeSequence(lines);
    },

    hide() {
      if (timer) clearInterval(timer);
      timer = null;
      card.hidden = true;
      card.classList.remove("arriving");
      for (const node of [indexEl, titleEl, ...rowsEl.children]) {
        node.classList.remove("typing");
      }
      progress.classList.remove("running");
    },
  };
}
